import config from 'config';
import { isArray } from 'lodash';

function replacePhoneNumber(str, phoneNumber) {
  phoneNumber = phoneNumber ?? '';
  if (typeof str === 'string') {
    return str.replace(/{phoneNumber}/g, phoneNumber);
  }
  return str;
}

/**
 * Retrieves store group-specific copy configuration from config.
 * This function looks up configuration based on a store group ID and returns
 * the appropriate copy, etc., with phone number placeholders replaced if provided.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.configKey - The configuration key to look up in the config (e.g., 'public.errorCopy')
 * @param {string|number} [options.storeGroupId] - The store group ID to find specific copy for
 * @param {string} [options.phoneNumber] - Phone number to replace {phoneNumber} placeholders in the copy
 * @returns {Object} Configuration object with copy text, excluding metadata keys
 *
 * @description
 * The function processes an array of copy configurations from the config and:
 * 1. **Finds store-specific copy**: Looks for an entry matching the provided storeGroupId
 * 2. **Falls back to default**: Uses the entry marked with `isBrandDefault: true` if no store-specific copy exists
 * 3. **Replaces placeholders**: Substitutes {phoneNumber} tokens with the provided phone number
 * 4. **Filters metadata**: Excludes `storeGroupId` and `isBrandDefault` from the returned object
 */
export const getStoreGroupCopyConfig = ({
  configKey,
  storeGroupId,
  phoneNumber,
}) => {
  let errorCopyArray = null;
  try {
    errorCopyArray = config.get(configKey);
  } catch (error) {
    // handle bad config key
    errorCopyArray = null;
  }
  const returnConfig = {};

  if (isArray(errorCopyArray)) {
    let defaultCopy = null;
    let sgCopy = null;

    for (const copyEntry of errorCopyArray) {
      if (!defaultCopy || copyEntry.isBrandDefault) {
        defaultCopy = copyEntry;
      }
      if (copyEntry.storeGroupId === storeGroupId) {
        sgCopy = copyEntry;
        break;
      }
    }

    if (!sgCopy) {
      sgCopy = defaultCopy;
    }

    for (const key in sgCopy) {
      // don't include config metadata keys in the return object
      if (!['storeGroupId', 'isBrandDefault'].includes(key)) {
        returnConfig[key] = replacePhoneNumber(sgCopy[key], phoneNumber);
      }
    }
  }

  return returnConfig;
};
